import { ref, computed, watch, toValue } from "vue";
import dayjs from "dayjs";

/**
 * Conflict resolution state + payload builders for series/single booking conflicts.
 *
 * @param {import('vue').MaybeRefOrGetter<object>} conflictData
 */
export function useConflictResolution(conflictData) {
	const selectedResolution = ref("cancellations");

	const data = computed(() => toValue(conflictData) || { hasConflict: false });

	const isSeriesBooking = computed(() => {
		const original = data.value?.originalData;
		if (!original) return false;

		return !!(
			original.rrulePattern ||
			original.pattern ||
			original.seriesId ||
			original.bookingType === "series"
		);
	});

	const resetDefaultResolution = () => {
		selectedResolution.value = isSeriesBooking.value
			? "cancellations"
			: "change-config";
	};

	watch(isSeriesBooking, resetDefaultResolution, { immediate: true });

	function toDateOnly(date) {
		return dayjs(date).format("YYYY-MM-DD");
	}

	function buildCancellationsPayload() {
		const conflict = data.value;
		const conflictingDates = (conflict.conflictingDates || []).map(toDateOnly);
		const closedDates = (conflict.closedDates || []).map(toDateOnly);
		const allCancellationDates = [...conflictingDates, ...closedDates];

		return {
			data: {
				...conflict.originalData,
				cancellations: [
					...(conflict.originalData.cancellations || []),
					...allCancellationDates,
				],
			},
			endpoint: conflict.endpoint,
			resolution: "cancellations",
		};
	}

	function resolve() {
		if (selectedResolution.value === "cancellations") {
			return buildCancellationsPayload();
		}
		console.warn("Unknown resolution type:", selectedResolution.value);
		return null;
	}

	function cancel() {
		selectedResolution.value = "cancellations";
	}

	return {
		selectedResolution,
		isSeriesBooking,
		resetDefaultResolution,
		resolve,
		cancel,
	};
}
